import type { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ProductionDeclarationSendRecipients } from '@/features/production-declaration/production-declaration-send-recipients';

export type ProductionDeclarationEmailAction = 'approve' | 'refuse';

type ProductionDeclarationSendEmailDialogProps = {
  open: boolean;
  action: ProductionDeclarationEmailAction;
  recipients: string[];
  recipientLabels: Record<string, string>;
  message: string;
  isSending: boolean;
  onOpenChange: (open: boolean) => void;
  onRecipientsChange: (nextRecipients: string[]) => void;
  onMessageChange: (nextMessage: string) => void;
  onSend: () => void;
};

export function ProductionDeclarationSendEmailDialog({
  open,
  action,
  recipients,
  recipientLabels,
  message,
  isSending,
  onOpenChange,
  onRecipientsChange,
  onMessageChange,
  onSend,
}: ProductionDeclarationSendEmailDialogProps) {
  const { t } = useTranslation('productionDeclaration');

  const title =
    action === 'approve'
      ? t('sendEmailModalApproveTitle')
      : t('sendEmailModalRefuseTitle');
  const submitLabel =
    action === 'approve'
      ? t('sendEmailModalApproveSubmit')
      : t('sendEmailModalRefuseSubmit');

  const handleOpenChange = (nextOpen: boolean) => {
    if (isSending) {
      return;
    }
    onOpenChange(nextOpen);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (
      event.key === 'Enter' &&
      !(event.target instanceof HTMLTextAreaElement)
    ) {
      event.preventDefault();
    }
  };

  return (
    <Dialog onOpenChange={handleOpenChange} open={open}>
      <DialogContent className='sm:max-w-3xl' onKeyDown={handleKeyDown}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className='flex flex-col gap-4'>
          <ProductionDeclarationSendRecipients
            isSending={isSending}
            onChange={onRecipientsChange}
            open={open}
            recipientLabels={recipientLabels}
            recipients={recipients}
          />
          <div className='flex flex-col gap-2'>
            <Label htmlFor='production-declaration-email-message'>
              {t('sendEmailModalMessage')}
            </Label>
            <Textarea
              className='field-sizing-fixed max-h-60 min-h-24 overflow-y-auto'
              id='production-declaration-email-message'
              onChange={(event) => onMessageChange(event.target.value)}
              rows={12}
              value={message}
            />
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button disabled={isSending} type='button' variant='outline'>
              {t('cancel', { ns: 'common' })}
            </Button>
          </DialogClose>
          <Button
            disabled={isSending || recipients.length === 0}
            onClick={onSend}
            type='button'
            variant={
              action === 'approve' ? 'declaration-primary' : 'destructive'
            }
          >
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
